import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API_BASE, { apiUrl } from "config/api";
import { getAccessToken } from "utils/auth";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import "../Settings/settings.css";
import AdminDataTab from "./AdminDataTab";

const fieldStyle = { display: "flex", flexDirection: "column", gap: 6 };
const cardStyle = {
  background: "rgba(12, 15, 22, 0.65)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
};
const labelStyle = { color: "#fff", fontSize: 13, opacity: 0.9 };
const inputStyle = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  color: "#f5f6f8",
  padding: "10px 12px",
  borderRadius: 10,
};
const buttonStyle = {
  padding: "10px 14px",
  borderRadius: 12,
  border: "none",
  background: "linear-gradient(135deg, #7BD5F5 0%, #4B86F7 100%)",
  color: "#08111b",
  fontWeight: 700,
  cursor: "pointer",
};
const selectStyle = {
  ...inputStyle,
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  paddingRight: 28,
  backgroundImage:
    "linear-gradient(45deg, transparent 50%, #7BD5F5 50%), linear-gradient(135deg, #7BD5F5 50%, transparent 50%)",
  backgroundPosition: "calc(100% - 16px) 55%, calc(100% - 11px) 55%",
  backgroundSize: "6px 6px, 6px 6px",
  backgroundRepeat: "no-repeat",
};

export default function AdminPanel() {
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
  const isDe = lang.startsWith("de");
  const isZh = lang.startsWith("zh");
  const locale = isDe ? "de-DE" : isZh ? "zh-CN" : "en-US";
  const tx = useCallback((en, de, zh) => {
    if (isDe) return de;
    if (isZh) return zh;
    return en;
  }, [isDe, isZh]);
  const roles = useMemo(
    () => [
      { label: tx("User", "Benutzer", "用户"), value: "USER" },
      { label: tx("Content manager", "Content-Manager", "内容管理员"), value: "CONTENT_MANAGER" },
      { label: tx("Admin", "Admin", "管理员"), value: "ADMIN" },
    ],
    [tx]
  );
  const [availableModules, setAvailableModules] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [groupForm, setGroupForm] = useState({ name: "", description: "", modules: {} });
  const [groupMembershipForm, setGroupMembershipForm] = useState({ groupId: "", userId: "" });
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [userFilters, setUserFilters] = useState({ role: "all", status: "all", module: "all", online: "all" });
  const [selectedUserId, setSelectedUserId] = useState("");
  const [userConnections, setUserConnections] = useState({ connections: [] });
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionsError, setConnectionsError] = useState("");
  const [userModulesForm, setUserModulesForm] = useState({ modules: {} });
  const [userRoleForm, setUserRoleForm] = useState({ role: "user" });
  const [activeTab, setActiveTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [analyticsSeries, setAnalyticsSeries] = useState(null);
  const [aiSeries, setAiSeries] = useState(null);
  const [kpiRange, setKpiRange] = useState("30d");
  const [granularity, setGranularity] = useState("day");
  const [metricSelection, setMetricSelection] = useState(["sessions", "active_users", "new_users", "conversions"]);
  const [locationRegion, setLocationRegion] = useState("world");
  const [dataMetrics, setDataMetrics] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState("");
  const [dataRange, setDataRange] = useState("30d");
  const [materialPipelineStatus, setMaterialPipelineStatus] = useState(null);
  const [materialPipelineStatusLoading, setMaterialPipelineStatusLoading] = useState(false);
  const [materialPipelineStatusError, setMaterialPipelineStatusError] = useState("");
  const [newsMetrics, setNewsMetrics] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState("");
  const [newsRange, setNewsRange] = useState("30d");
  const [reportsMetrics, setReportsMetrics] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState("");
  const [reportsRange, setReportsRange] = useState("30d");
  const [materialInsights, setMaterialInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsError, setInsightsError] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [materialDetail, setMaterialDetail] = useState(null);
  const [materialDetailLoading, setMaterialDetailLoading] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [sectorsLoading, setSectorsLoading] = useState(false);
  const [sectorsError, setSectorsError] = useState("");
  const [selectedSectorId, setSelectedSectorId] = useState(null);
  const [sectorDetail, setSectorDetail] = useState(null);
  const [sectorDetailLoading, setSectorDetailLoading] = useState(false);
  const [sectorForm, setSectorForm] = useState({ name: "", imageFile: null });
  const [sectorEditForm, setSectorEditForm] = useState({ name: "", imageFile: null, imagePreview: "" });
  const [materialSearch, setMaterialSearch] = useState("");
  const [materialSearchResults, setMaterialSearchResults] = useState([]);
  const [materialSearchLoading, setMaterialSearchLoading] = useState(false);
  const [materialSearchError, setMaterialSearchError] = useState("");
  const [materialAddTarget, setMaterialAddTarget] = useState("sector");
  const [listForm, setListForm] = useState({ name: "", description: "", sort_order: 0 });
  const [listDrafts, setListDrafts] = useState({});
  const [translationModalOpen, setTranslationModalOpen] = useState(false);
  const [translationLoading, setTranslationLoading] = useState(false);
  const [translationSaving, setTranslationSaving] = useState(false);
  const [translationError, setTranslationError] = useState("");
  const [translationEntity, setTranslationEntity] = useState({ type: "", id: null });
  const [translationSourceText, setTranslationSourceText] = useState("");
  const [translationInputs, setTranslationInputs] = useState({ en: "", zh: "" });
  const chartColors = useMemo(() => ["#7BD5F5", "#9B7BF5", "#6ADFA0", "#F2E96B"], []);
  const regionMeta = useMemo(() => ([
    { key: "eu", label: "EU" },
    { key: "asia", label: tx("Asia", "Asien", "亚洲") },
    { key: "america", label: tx("America", "Amerika", "美洲") },
  ]), [tx]);
  const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
  const countryCoords = useMemo(
    () => ({
      germany: [10.45, 51.17],
      poland: [19.145, 52.237],
      austria: [14.55, 47.516],
      denmark: [10.0, 56.263],
      usa: [-98.35, 39.5],
      america: [-98.35, 39.5],
      canada: [-106.35, 56.13],
      mexico: [-102.55, 23.63],
      asia: [90, 30],
      india: [78.9629, 20.5937],
      china: [104.1954, 35.8617],
      japan: [138.2529, 36.2048],
    }),
    [],
  );

  const authHeaders = useCallback(() => {
    const token = getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);
  const numberFmt = (val, opts = {}) => {
    if (val === null || val === undefined) return "—";
    // Standard 2 Nachkommastellen; wer maximumFractionDigits kleiner setzt (z. B. 0 fuer
    // Anzahlen), bekommt das Minimum mit abgesenkt -- sonst wirft Intl einen RangeError
    // (min > max) und der ganze Admin-Reiter stuerzt ab.
    const maximumFractionDigits = opts.maximumFractionDigits ?? 2;
    const minimumFractionDigits = Math.min(opts.minimumFractionDigits ?? 2, maximumFractionDigits);
    return new Intl.NumberFormat(locale, { ...opts, minimumFractionDigits, maximumFractionDigits }).format(val);
  };
  const percentFmt = (val) => (val === null || val === undefined ? "—" : `${Number(val).toFixed(2)}%`);
  const durationFmt = (seconds) => {
    if (!seconds && seconds !== 0) return "—";
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins}m`;
    return `${(mins / 60).toFixed(2)}h`;
  };

  useEffect(() => {
    let cancelled = false;
    const fetchMeta = async () => {
      try {
        const [modulesRes, groupsRes, usersRes, analyticsRes] = await Promise.all([
          fetch(apiUrl("/api/permissions/modules"), {
            credentials: "include",
            headers: authHeaders(),
          }),
          fetch(apiUrl("/api/permissions/groups"), {
            credentials: "include",
            headers: authHeaders(),
          }),
          fetch(apiUrl("/api/users"), {
            credentials: "include",
            headers: authHeaders(),
          }),
          fetch(apiUrl("/api/admin/analytics"), {
            credentials: "include",
            headers: authHeaders(),
          }),
        ]);
        if (!cancelled && modulesRes.ok) {
          const data = await modulesRes.json();
          setAvailableModules(data?.modules || []);
        }
        if (!cancelled && groupsRes.ok) {
          const groups = await groupsRes.json();
          setPermissionGroups(groups || []);
        }
        if (!cancelled && usersRes.ok) {
          const list = await usersRes.json();
          setUsers(Array.isArray(list) ? list : []);
          setError("");
        } else if (!cancelled) {
          setError(tx("Could not load users. Check your permissions.", "Benutzer konnten nicht geladen werden. Berechtigungen prüfen.", "无法加载用户，请检查权限。"));
        }
        if (!cancelled && analyticsRes.ok) {
          const data = await analyticsRes.json();
          setAnalytics(data);
        }
      } catch {
        if (!cancelled) setError(tx("Unable to load admin data. Please retry.", "Admin-Daten konnten nicht geladen werden. Bitte erneut versuchen.", "无法加载管理数据，请重试。"));
      }
    };
    fetchMeta();
    return () => {
      cancelled = true;
    };
  }, [authHeaders]);

  const fetchAnalyticsSeries = useCallback(async (rangeKey = "30d", gran = "day") => {
    const now = new Date();
    const days = rangeKey === "7d" ? 7 : rangeKey === "90d" ? 90 : 30;
    const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    const params = new URLSearchParams({
      start_date: start.toISOString(),
      end_date: now.toISOString(),
      granularity: gran,
    });
    try {
      const [seriesRes, aiRes] = await Promise.all([
        fetch(apiUrl(`/api/admin/analytics/series?${params}`), {
          credentials: "include",
          headers: authHeaders(),
        }),
        fetch(apiUrl(`/api/admin/analytics/ai_series?${params}`), {
          credentials: "include",
          headers: authHeaders(),
        }),
      ]);
      if (seriesRes.ok) {
        setAnalyticsSeries(await seriesRes.json());
      }
      if (aiRes.ok) {
        setAiSeries(await aiRes.json());
      }
    } catch (e) {
      console.warn("analytics series fetch failed", e);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchAnalyticsSeries(kpiRange, granularity);
  }, [fetchAnalyticsSeries, kpiRange, granularity]);

  const loadConnections = async (userId) => {
    if (!userId) return;
    setConnectionsLoading(true);
    setConnectionsError("");
    try {
      const res = await fetch(apiUrl(`/api/users/${userId}/connections`), {
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) {
        throw new Error(tx("Could not load connections", "Verbindungen konnten nicht geladen werden", "无法加载连接"));
      }
      const data = await res.json();
      setUserConnections(data || { connections: [] });
    } catch (e) {
      setConnectionsError(e.message || tx("Failed to load connections", "Verbindungen konnten nicht geladen werden", "加载连接失败"));
      setUserConnections({ connections: [] });
    } finally {
      setConnectionsLoading(false);
    }
  };

  const fetchDataMetrics = useCallback(async () => {
    setDataLoading(true);
    setDataError("");
    try {
      const now = new Date();
      const days = dataRange === "7d" ? 7 : dataRange === "90d" ? 90 : 30;
      const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      const params = new URLSearchParams({
        start_date: start.toISOString(),
        end_date: now.toISOString(),
      });
      const res = await fetch(apiUrl(`/api/admin/data/metrics?${params}`), {
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) {
        throw new Error(tx("Could not load data metrics", "Datenmetriken konnten nicht geladen werden", "无法加载数据指标"));
      }
      setDataMetrics(await res.json());
    } catch (e) {
      setDataError(e.message || tx("Failed to load data metrics", "Datenmetriken konnten nicht geladen werden", "加载数据指标失败"));
      setDataMetrics(null);
    } finally {
      setDataLoading(false);
    }
  }, [authHeaders, dataRange]);

  const fetchMaterialPipelineStatus = useCallback(async () => {
    setMaterialPipelineStatusLoading(true);
    setMaterialPipelineStatusError("");
    try {
      const res = await fetch(apiUrl("/api/admin/data/material-pipeline/status"), {
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) {
        throw new Error(tx("Could not load material pipeline status", "Material-Pipeline-Status konnte nicht geladen werden", "无法加载材料管道状态"));
      }
      setMaterialPipelineStatus(await res.json());
    } catch (e) {
      setMaterialPipelineStatusError(
        e.message || tx("Failed to load material pipeline status", "Material-Pipeline-Status konnte nicht geladen werden", "加载材料管道状态失败")
      );
      setMaterialPipelineStatus(null);
    } finally {
      setMaterialPipelineStatusLoading(false);
    }
  }, [authHeaders, tx]);

  const getRangeStart = useCallback((rangeKey) => {
    const now = new Date();
    const days = rangeKey === "7d" ? 7 : rangeKey === "90d" ? 90 : 30;
    return new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
  }, []);

  const fetchNewsMetrics = useCallback(async () => {
    setNewsLoading(true);
    setNewsError("");
    try {
      const now = new Date();
      const start = getRangeStart(newsRange);
      const params = new URLSearchParams({
        start_date: start.toISOString(),
        end_date: now.toISOString(),
      });
      const res = await fetch(apiUrl(`/api/admin/data/news/metrics?${params}`), {
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) {
        throw new Error(tx("Could not load news metrics", "News-Metriken konnten nicht geladen werden", "无法加载新闻指标"));
      }
      setNewsMetrics(await res.json());
    } catch (e) {
      setNewsError(e.message || tx("Failed to load news metrics", "News-Metriken konnten nicht geladen werden", "加载新闻指标失败"));
      setNewsMetrics(null);
    } finally {
      setNewsLoading(false);
    }
  }, [authHeaders, getRangeStart, newsRange, tx]);

  const fetchReportsMetrics = useCallback(async () => {
    setReportsLoading(true);
    setReportsError("");
    try {
      const now = new Date();
      const start = getRangeStart(reportsRange);
      const params = new URLSearchParams({
        start_date: start.toISOString(),
        end_date: now.toISOString(),
      });
      const res = await fetch(apiUrl(`/api/admin/data/reports/metrics?${params}`), {
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) {
        throw new Error(tx("Could not load reports metrics", "Berichtsmetriken konnten nicht geladen werden", "无法加载报告指标"));
      }
      setReportsMetrics(await res.json());
    } catch (e) {
      setReportsError(e.message || tx("Failed to load reports metrics", "Berichtsmetriken konnten nicht geladen werden", "加载报告指标失败"));
      setReportsMetrics(null);
    } finally {
      setReportsLoading(false);
    }
  }, [authHeaders, getRangeStart, reportsRange, tx]);

  const fetchMaterialInsights = useCallback(async () => {
    setInsightsLoading(true);
    setInsightsError("");
    try {
      const res = await fetch(apiUrl("/api/admin/data/materials/insights"), {
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) {
        throw new Error(tx("Could not load material insights", "Material-Insights konnten nicht geladen werden", "无法加载材料洞察"));
      }
      setMaterialInsights(await res.json());
    } catch (e) {
      setInsightsError(e.message || tx("Failed to load material insights", "Material-Insights konnten nicht geladen werden", "加载材料洞察失败"));
      setMaterialInsights(null);
    } finally {
      setInsightsLoading(false);
    }
  }, [authHeaders]);

  const fetchMaterialDetail = useCallback(
    async (code) => {
      if (!code) return;
      setMaterialDetailLoading(true);
      try {
        const res = await fetch(apiUrl(`/api/admin/data/materials/${code}`), {
          credentials: "include",
          headers: authHeaders(),
        });
        if (!res.ok) {
          throw new Error(tx("Could not load material detail", "Materialdetails konnten nicht geladen werden", "无法加载材料详情"));
        }
        setMaterialDetail(await res.json());
      } catch (e) {
        setMaterialDetail(null);
      } finally {
        setMaterialDetailLoading(false);
      }
    },
    [authHeaders]
  );

  const fetchSectors = useCallback(async () => {
    setSectorsLoading(true);
    setSectorsError("");
    try {
      const res = await fetch(apiUrl("/api/admin/data/sectors"), {
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(tx("Could not load sectors", "Sektoren konnten nicht geladen werden", "无法加载行业"));
      const data = await res.json();
      setSectors(Array.isArray(data) ? data : []);
    } catch (e) {
      setSectorsError(e.message || tx("Failed to load sectors", "Sektoren konnten nicht geladen werden", "加载行业失败"));
      setSectors([]);
    } finally {
      setSectorsLoading(false);
    }
  }, [authHeaders]);

  const loadSectorDetail = useCallback(
    async (sectorId) => {
      if (!sectorId) return;
      setSelectedSectorId(sectorId);
      setSectorDetailLoading(true);
      try {
        const res = await fetch(apiUrl(`/api/admin/data/sectors/${sectorId}`), {
          credentials: "include",
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(tx("Could not load sector detail", "Sektordetails konnten nicht geladen werden", "无法加载行业详情"));
        const data = await res.json();
        setSectorDetail(data);
        setSectorEditForm({ name: data?.name || "", imageFile: null, imagePreview: data?.image || "" });
        const drafts = {};
        (data?.lists || []).forEach((l) => {
          drafts[l.id] = { name: l.name || "", description: l.description || "", sort_order: l.sort_order ?? 0 };
        });
        setListDrafts(drafts);
      } catch (e) {
        setSectorDetail(null);
      } finally {
        setSectorDetailLoading(false);
      }
    },
    [authHeaders]
  );

  const createSector = async (e) => {
    e.preventDefault();
    if (!sectorForm.name.trim()) return;
    try {
      const fd = new FormData();
      fd.append("name", sectorForm.name.trim());
      if (sectorForm.imageFile) {
        fd.append("image_file", sectorForm.imageFile);
      }
      const res = await fetch(apiUrl("/api/admin/data/sectors"), {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) throw new Error(tx("Could not create sector", "Sektor konnte nicht erstellt werden", "无法创建行业"));
      const detail = await res.json();
      setSectorForm({ name: "", imageFile: null });
      await fetchSectors();
      setSelectedSectorId(detail?.id);
      setSectorDetail(detail);
      setSectorEditForm({ name: detail?.name || "", imageFile: null, imagePreview: detail?.image || "" });
    } catch (e) {
      setSectorsError(e.message || tx("Failed to create sector", "Sektor konnte nicht erstellt werden", "创建行业失败"));
    }
  };

  const updateSector = async (e) => {
    e.preventDefault();
    if (!sectorDetail?.id) return;
    try {
      const fd = new FormData();
      if (sectorEditForm.name) fd.append("name", sectorEditForm.name);
      if (sectorEditForm.imageFile) fd.append("image_file", sectorEditForm.imageFile);
      const res = await fetch(apiUrl(`/api/admin/data/sectors/${sectorDetail.id}`), {
        method: "PATCH",
        credentials: "include",
        headers: authHeaders(),
        body: fd,
      });
      if (!res.ok) throw new Error(tx("Could not update sector", "Sektor konnte nicht aktualisiert werden", "无法更新行业"));
      const detail = await res.json();
      setSectorDetail(detail);
      setSectorEditForm({ name: detail?.name || "", imageFile: null, imagePreview: detail?.image || sectorEditForm.imagePreview });
      await fetchSectors();
    } catch (e) {
      setSectorsError(e.message || tx("Failed to update sector", "Sektor konnte nicht aktualisiert werden", "更新行业失败"));
    }
  };

  const deleteSector = async (sectorId) => {
    if (!sectorId) return;
    if (!window.confirm(tx("Delete this sector? This will unlink materials and lists.", "Diesen Sektor löschen? Materialien und Listen werden entkoppelt.", "删除该行业？这将取消材料和列表的关联。"))) return;
    try {
      const res = await fetch(apiUrl(`/api/admin/data/sectors/${sectorId}`), {
        method: "DELETE",
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(tx("Could not delete sector", "Sektor konnte nicht gelöscht werden", "无法删除行业"));
      setSectorDetail(null);
      setSelectedSectorId(null);
      await fetchSectors();
    } catch (e) {
      setSectorsError(e.message || tx("Failed to delete sector", "Sektor konnte nicht gelöscht werden", "删除行业失败"));
    }
  };

  const replaceSectorMaterials = async (sectorId, ids) => {
    if (!sectorId) return;
    try {
      const res = await fetch(apiUrl(`/api/admin/data/sectors/${sectorId}/materials`), {
        method: "PUT",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ material_ids: ids }),
      });
      if (!res.ok) throw new Error(tx("Could not update materials", "Materialien konnten nicht aktualisiert werden", "无法更新材料"));
      const detail = await res.json();
      setSectorDetail(detail);
      await fetchSectors();
    } catch (e) {
      setSectorsError(e.message || tx("Failed to update materials", "Materialien konnten nicht aktualisiert werden", "更新材料失败"));
    }
  };

  const removeMaterialFromSector = async (mid) => {
    if (!sectorDetail?.id) return;
    const ids = (sectorDetail.materials || []).map((m) => m.id).filter((id) => id !== mid);
    await replaceSectorMaterials(sectorDetail.id, ids);
  };

  const addMaterialToList = async (listId, material) => {
    if (!sectorDetail?.id || !listId || !material?.id) return;
    const list = (sectorDetail.lists || []).find((l) => l.id === listId);
    const existing = new Set((list?.materials || []).map((m) => m.id));
    if (existing.has(material.id)) return;
    const ids = [...existing, material.id];
    try {
      await fetch(apiUrl(`/api/admin/data/sectors/${sectorDetail.id}/lists/${listId}`), {
        method: "PATCH",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ material_ids: ids }),
      });
      await loadSectorDetail(sectorDetail.id);
    } catch (e) {
      setSectorsError(e.message || tx("Failed to add material to list", "Material konnte nicht zur Liste hinzugefügt werden", "将材料添加到列表失败"));
    }
  };

  const removeMaterialFromList = async (listId, materialId) => {
    if (!sectorDetail?.id || !listId || !materialId) return;
    const list = (sectorDetail.lists || []).find((l) => l.id === listId);
    const ids = (list?.materials || []).map((m) => m.id).filter((id) => id !== materialId);
    try {
      await fetch(apiUrl(`/api/admin/data/sectors/${sectorDetail.id}/lists/${listId}`), {
        method: "PATCH",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ material_ids: ids }),
      });
      await loadSectorDetail(sectorDetail.id);
    } catch (e) {
      setSectorsError(e.message || tx("Failed to remove material from list", "Material konnte nicht aus der Liste entfernt werden", "从列表移除材料失败"));
    }
  };

  const addMaterialToSector = async (material) => {
    if (!sectorDetail?.id || !material?.id) return;
    const existing = new Set((sectorDetail.materials || []).map((m) => m.id));
    if (existing.has(material.id)) return;
    const ids = [...existing, material.id];
    await replaceSectorMaterials(sectorDetail.id, Array.from(ids));
  };

  const searchMaterials = useCallback(
    async (query) => {
      const q = (query || "").trim();
      setMaterialSearch(q);
      if (!q) {
        setMaterialSearchResults([]);
        return;
      }
      setMaterialSearchLoading(true);
      setMaterialSearchError("");
      try {
        const params = new URLSearchParams({ query: q, type: "text", limit: 12 });
        const res = await fetch(apiUrl(`/api/materials/search?${params.toString()}`), {
          credentials: "include",
          headers: authHeaders(),
        });
        if (!res.ok) throw new Error(tx("Search failed", "Suche fehlgeschlagen", "搜索失败"));
        const data = await res.json();
        const results = Array.isArray(data?.results) ? data.results : Array.isArray(data) ? data : [];
        setMaterialSearchResults(results);
      } catch (e) {
        setMaterialSearchError(e.message || tx("Search failed", "Suche fehlgeschlagen", "搜索失败"));
        setMaterialSearchResults([]);
      } finally {
        setMaterialSearchLoading(false);
      }
    },
    [authHeaders]
  );

  const createList = async (e) => {
    e.preventDefault();
    if (!sectorDetail?.id || !listForm.name.trim()) return;
    try {
      const payload = {
        name: listForm.name.trim(),
        description: listForm.description || null,
        sort_order: Number(listForm.sort_order) || 0,
      };
      const res = await fetch(apiUrl(`/api/admin/data/sectors/${sectorDetail.id}/lists`), {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(tx("Could not create list", "Liste konnte nicht erstellt werden", "无法创建列表"));
      setListForm({ name: "", description: "", sort_order: 0 });
      await loadSectorDetail(sectorDetail.id);
      await fetchSectors();
    } catch (e) {
      setSectorsError(e.message || tx("Failed to create list", "Liste konnte nicht erstellt werden", "创建列表失败"));
    }
  };

  const onListDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;
    const sortedLists = [...(sectorDetail.lists || [])].sort((a, b) => a.sort_order - b.sort_order);
    const reordered = [...sortedLists];
    const [removed] = reordered.splice(source.index, 1);
    reordered.splice(destination.index, 0, removed);
    const updatedLists = reordered.map((list, index) => ({
      ...list,
      sort_order: index,
    }));
    setSectorDetail((prev) => ({ ...prev, lists: updatedLists }));
    const newDrafts = { ...listDrafts };
    updatedLists.forEach((list, index) => {
      newDrafts[list.id] = {
        ...newDrafts[list.id],
        name: list.name,
        description: list.description,
        sort_order: index,
      };
    });
    setListDrafts(newDrafts);
    updatedLists.forEach((list) => {
      saveListDraft(list.id, { sort_order: list.sort_order });
    });
  };

  const saveListDraft = async (listId, data) => {
    if (!sectorDetail?.id || !listId) return;
    const draft = listDrafts[listId] || {};
    const payload = data || {
      name: draft.name,
      description: draft.description,
      sort_order: draft.sort_order,
    };
    try {
      const res = await fetch(apiUrl(`/api/admin/data/sectors/${sectorDetail.id}/lists/${listId}`), {
        method: "PATCH",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(tx("Could not update list", "Liste konnte nicht aktualisiert werden", "无法更新列表"));
    } catch (e) {
      setSectorsError(e.message || tx("Failed to update list", "Liste konnte nicht aktualisiert werden", "更新列表失败"));
    }
  };

  const deleteList = async (listId) => {
    if (!sectorDetail?.id || !listId) return;
    if (!window.confirm(tx("Delete this list?", "Diese Liste löschen?", "删除此列表？"))) return;
    try {
      const res = await fetch(apiUrl(`/api/admin/data/sectors/${sectorDetail.id}/lists/${listId}`), {
        method: "DELETE",
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(tx("Could not delete list", "Liste konnte nicht gelöscht werden", "无法删除列表"));
      setSectorDetail((prev) => ({
        ...prev,
        lists: prev.lists.filter((l) => l.id !== listId),
      }));
    } catch (e) {
      setSectorsError(e.message || tx("Failed to delete list", "Liste konnte nicht gelöscht werden", "删除列表失败"));
    }
  };

  useEffect(() => {
    if (activeTab === "data") {
      fetchDataMetrics();
      fetchMaterialInsights();
      fetchSectors();
      fetchMaterialPipelineStatus();
    }
  }, [activeTab, fetchDataMetrics, fetchMaterialInsights, fetchSectors, fetchMaterialPipelineStatus, dataRange]);

  useEffect(() => {
    if (activeTab !== "data") return;
    const timer = setInterval(() => {
      fetchMaterialPipelineStatus();
    }, 20000);
    return () => clearInterval(timer);
  }, [activeTab, fetchMaterialPipelineStatus]);

  useEffect(() => {
    if (activeTab !== "data") return;
    if (sectorDetail || sectorsLoading) return;
    if (sectors.length > 0 && !selectedSectorId) {
      loadSectorDetail(sectors[0].id);
    }
  }, [activeTab, sectors, selectedSectorId, sectorDetail, sectorsLoading, loadSectorDetail]);

  useEffect(() => {
    if (activeTab === "news") {
      fetchNewsMetrics();
    }
  }, [activeTab, newsRange, fetchNewsMetrics]);

  useEffect(() => {
    if (activeTab === "reports") {
      fetchReportsMetrics();
    }
  }, [activeTab, reportsRange, fetchReportsMetrics]);

  const applyUserSelection = (user) => {
    if (!user) return;
    const moduleMap = {};
    (user.allowed_modules || []).forEach((m) => {
      moduleMap[m] = true;
    });
    setSelectedUserId(user.id);
    setUserRoleForm({ role: (user.role || "USER").toUpperCase() });
    setUserModulesForm({ modules: moduleMap });
    loadConnections(user.id);
  };

  const roleStats = useMemo(() => {
    if (analytics?.roles) return analytics.roles;
    const buckets = { user: 0, content_manager: 0, admin: 0 };
    users.forEach((u) => {
      const r = (u.role || "").toLowerCase();
      buckets[r] = (buckets[r] || 0) + 1;
    });
    return buckets;
  }, [analytics, users]);

  const moduleStats = useMemo(() => {
    if (analytics?.modules) return analytics.modules;
    const counts = {};
    users.forEach((u) => {
      (u.allowed_modules || []).forEach((m) => {
        counts[m] = (counts[m] || 0) + 1;
      });
    });
    const totalAssignments = Object.values(counts).reduce((sum, c) => sum + c, 0);
    const avg = users.length ? (totalAssignments / Math.max(users.length, 1)).toFixed(2) : "0.0";
    return { counts, avgPerUser: avg, totalUsers: users.length };
  }, [analytics, users]);

  const reportsStatusChartData = useMemo(
    () =>
      (reportsMetrics?.status_distribution || []).map((row) => ({
        label: String(row.status || "unknown"),
        value: Number(row.count || 0),
      })),
    [reportsMetrics]
  );

  const insightsWindowDays = materialInsights?.meta?.window_days || 30;
  const insightsFallbackUsed = Boolean(materialInsights?.meta?.fallback_used);
  const insightsFallbackReason = materialInsights?.meta?.fallback_reason;

  const filteredUsers = useMemo(() => {
    const q = (userSearch || "").toLowerCase();
    const roleFilter = (userFilters.role || "all").toLowerCase();
    const statusFilter = (userFilters.status || "all").toLowerCase();
    const moduleFilter = userFilters.module || "all";
    const onlineFilter = (userFilters.online || "all").toLowerCase();
    return users.filter((u) => {
      const name = `${u.first_name || ""} ${u.last_name || ""}`.toLowerCase();
      const email = (u.email || "").toLowerCase();
      if (q && !name.includes(q) && !email.includes(q)) return false;
      if (roleFilter !== "all" && (u.role || "").toLowerCase() !== roleFilter) return false;
      if (statusFilter !== "all" && (u.status || "").toLowerCase() !== statusFilter) return false;
      if (moduleFilter !== "all") {
        const mods = u.allowed_modules || [];
        if (!mods.includes(moduleFilter)) return false;
      }
      if (onlineFilter !== "all") {
        const isOnline = !!u.online;
        if (onlineFilter === "online" && !isOnline) return false;
        if (onlineFilter === "offline" && isOnline) return false;
      }
      return true;
    });
  }, [users, userSearch, userFilters]);

  const renderSparkBar = (value, max = 1, color = "#4B86F7") => {
    const pct = Math.min(100, Math.round((value / Math.max(max, 1)) * 100));
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, height: 8, background: "rgba(255,255,255,0.08)", borderRadius: 999 }}>
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              background: color,
              borderRadius: 999,
              transition: "width 0.25s ease",
            }}
          />
        </div>
        <span style={{ color: "#cfe4ff", fontSize: 12 }}>{value}</span>
      </div>
    );
  };

  const renderOnlineBadge = (user) => {
    if (!user) return null;
    const online = !!user.online;
    const color = online ? "#6ADFA0" : "#f8b4b4";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 8px",
          borderRadius: 999,
          background: "rgba(255,255,255,0.06)",
          color,
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 0 2px rgba(255,255,255,0.07)` }} />
        {online ? tx("Online", "Online", "在线") : tx("Offline", "Offline", "离线")}
      </span>
    );
  };

  const renderStatusBadge = (user) => {
    if (!user) return null;
    const status = (user.status || "").toLowerCase();
    let label = status ? status.charAt(0).toUpperCase() + status.slice(1) : tx("Unknown", "Unbekannt", "未知");
    let color = "#7BD5F5";
    if (status === "inactive") color = "#e0aaff";
    if (status === "initializing") color = "#f2e96b";
    if (status === "active") color = "#7BD5F5";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 8px",
          borderRadius: 999,
          background: "rgba(255,255,255,0.06)",
          color,
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 0 2px rgba(255,255,255,0.07)` }} />
        {label}
      </span>
    );
  };

  const translationButton = (entityType, entityId, fallbackText) => (
    <button
      type="button"
      onClick={() => openTranslationModal(entityType, entityId, fallbackText)}
      title={tx("Edit EN/ZH translations", "EN/ZH-Übersetzungen bearbeiten", "编辑英文/中文翻译")}
      style={{
        border: "1px solid rgba(255,255,255,0.16)",
        background: "rgba(255,255,255,0.06)",
        color: "#fff",
        borderRadius: 8,
        padding: "2px 8px",
        lineHeight: 1.2,
        cursor: "pointer",
        fontSize: 12,
      }}
    >
      🚩
    </button>
  );

  const postJSON = async (endpoint, method, body) => {
    setLoading(true);
    setMessage("");
    setError("");
    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(body),
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === "string" ? data.detail : res.statusText);
      }
      setMessage(tx("Saved successfully.", "Erfolgreich gespeichert.", "保存成功。"));
      return data;
    } catch (err) {
      setError(err.message || tx("Request failed", "Anfrage fehlgeschlagen", "请求失败"));
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const openTranslationModal = useCallback(async (entityType, entityId, fallbackText = "") => {
    if (!entityType || !entityId) return;
    setTranslationEntity({ type: entityType, id: entityId });
    setTranslationError("");
    setTranslationSourceText(fallbackText || "");
    setTranslationInputs({ en: "", zh: "" });
    setTranslationModalOpen(true);
    setTranslationLoading(true);
    try {
      const res = await fetch(apiUrl(`/api/admin/data/translations/${entityType}/${entityId}?field_name=name`), {
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(tx("Could not load translations", "Übersetzungen konnten nicht geladen werden", "无法加载翻译"));
      const data = await res.json();
      setTranslationSourceText(data?.source_text || fallbackText || "");
      setTranslationInputs({
        en: data?.translations?.en || "",
        zh: data?.translations?.zh || "",
      });
    } catch (e) {
      setTranslationError(e.message || tx("Failed to load translations", "Übersetzungen konnten nicht geladen werden", "加载翻译失败"));
    } finally {
      setTranslationLoading(false);
    }
  }, [authHeaders, tx]);

  const saveTranslationModal = useCallback(async () => {
    if (!translationEntity?.type || !translationEntity?.id) return;
    setTranslationSaving(true);
    setTranslationError("");
    try {
      const res = await fetch(apiUrl(`/api/admin/data/translations/${translationEntity.type}/${translationEntity.id}?field_name=name`), {
        method: "PATCH",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          en: translationInputs.en,
          zh: translationInputs.zh,
        }),
      });
      if (!res.ok) throw new Error(tx("Could not save translations", "Übersetzungen konnten nicht gespeichert werden", "无法保存翻译"));
      await res.json();
      if (activeTab === "data") {
        if (selectedSectorId) await loadSectorDetail(selectedSectorId);
        await fetchSectors();
      }
      setTranslationModalOpen(false);
    } catch (e) {
      setTranslationError(e.message || tx("Failed to save translations", "Übersetzungen konnten nicht gespeichert werden", "保存翻译失败"));
    } finally {
      setTranslationSaving(false);
    }
  }, [translationEntity, translationInputs, authHeaders, tx, activeTab, selectedSectorId, loadSectorDetail, fetchSectors]);

  const handleUserRoleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      setError(tx("Select a user first.", "Wählen Sie zuerst einen Benutzer.", "请先选择用户。"));
      return;
    }
    await postJSON(`/api/users/${selectedUserId}/role`, "PUT", { role: (userRoleForm.role || "").toUpperCase() });
  };

  const handleUserModulesSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUserId) {
      setError(tx("Select a user first.", "Wählen Sie zuerst einen Benutzer.", "请先选择用户。"));
      return;
    }
    const modules = Object.entries(userModulesForm.modules || {})
      .filter(([, checked]) => checked)
      .map(([module]) => ({ module, is_allowed: true }));
    await postJSON(`/api/users/${selectedUserId}/modules`, "PUT", { modules });
  };

  const handleGroupCreate = async (e) => {
    e.preventDefault();
    if (!groupForm.name) return;
    const modules = Object.entries(groupForm.modules || {})
      .filter(([, checked]) => checked)
      .map(([module]) => ({ module, is_allowed: true }));
    const created = await postJSON(`/api/permissions/groups`, "POST", {
      name: groupForm.name,
      description: groupForm.description,
      modules,
    });
    setPermissionGroups((prev) => {
      const next = [...prev];
      const idx = next.findIndex((g) => g.id === created.id);
      if (idx >= 0) {
        next[idx] = created;
        return next;
      }
      return [created, ...next];
    });
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!groupMembershipForm.groupId || !groupMembershipForm.userId) return;
    const updated = await postJSON(
      `/api/permissions/groups/${groupMembershipForm.groupId}/members/${groupMembershipForm.userId}`,
      "POST",
      {}
    );
    setPermissionGroups((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
  };

  const selectedUser = users.find((u) => u.id === selectedUserId);
  const timeSeries = useMemo(() => {
    const primary = analyticsSeries?.series || [];
    if (primary.length) {
      return primary.map((p) => ({
        ts: p.ts,
        sessions: p.sessions,
        active_users: p.active_users,
        new_users: p.new_users,
        conversions: p.conversions,
        pageviews: p.pageviews,
        pageview_users: p.pageview_users,
        dateObj: p.ts ? new Date(p.ts) : null,
      }));
    }
    return (analytics?.time_series || []).map((p) => ({
      ts: p.ts,
      sessions: p.sessions,
      active_users: p.active_users,
      new_users: p.new_users,
      conversions: p.conversions,
      pageviews: p.pageviews,
      pageview_users: p.pageview_users,
      dateObj: p.ts ? new Date(p.ts) : null,
    }));
  }, [analytics, analyticsSeries]);

  const filteredTimeSeries = useMemo(() => {
    if (!timeSeries.length) return [];
    const now = new Date();
    const days = kpiRange === "7d" ? 7 : kpiRange === "90d" ? 90 : 30;
    const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    // keep only points within range and reduce to one per bucket (use provided series buckets)
    const byDay = {};
    timeSeries.forEach((p) => {
      if (!p.ts) return;
      const tsDate = p.dateObj || new Date(p.ts);
      if (tsDate < cutoff) return;
      const key = tsDate.toISOString().split("T")[0]; // YYYY-MM-DD
      byDay[key] = { ...p, ts: tsDate.toISOString() };
    });
    const daily = Object.values(byDay).sort((a, b) => new Date(a.ts) - new Date(b.ts));
    // fill missing days in range with zeroes so the chart keeps the timeline shape
    const filled = [];
    for (let i = days - 1; i >= 0; i -= 1) {
      const d = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const key = d.toISOString().split("T")[0];
      const found = byDay[key];
      filled.push(
        found || {
          ts: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12).toISOString(),
          sessions: 0,
          active_users: 0,
          new_users: 0,
          conversions: 0,
          pageviews: 0,
          pageview_users: 0,
        }
      );
    }
    return filled;
  }, [timeSeries, kpiRange]);

  const deviceChartData = useMemo(() => analytics?.devices || [], [analytics]);
  const browserChartData = useMemo(() => analytics?.browsers || [], [analytics]);
  const serverLatest = analytics?.server?.latest || {};
  const aiUsage = analytics?.ai_usage || {};
  const serverSeriesData = useMemo(() => {
    const raw = analytics?.server?.series || [];
    if (raw && raw.length >= 2) return raw;
    // fallback: synthesize a tiny sparkline from latest snapshot so the chart area is not empty
    if (serverLatest && (serverLatest.cpu || serverLatest.disk || serverLatest.net)) {
      return [
        { ts: "t-2", cpu: serverLatest.cpu, disk: serverLatest.disk, net: serverLatest.net },
        { ts: "t-1", cpu: serverLatest.cpu, disk: serverLatest.disk, net: serverLatest.net },
      ];
    }
    return [];
  }, [analytics, serverLatest]);

  const locationMarkers = useMemo(() => {
    const locs = analytics?.locations || {};
    const points = [];
    regionMeta.forEach((region) => {
      (locs[region.key] || []).forEach(([label, value]) => {
        const coord = countryCoords[label?.toLowerCase()] || countryCoords[region.key];
        if (coord) {
          points.push({
            name: label,
            value,
            coordinates: coord,
            region: region.label,
          });
        }
      });
    });
    return points;
  }, [analytics, countryCoords, regionMeta]);
  const filteredMarkers = useMemo(() => {
    if (locationRegion === "world") return locationMarkers;
    const regionLabel = locationRegion.toLowerCase();
    return locationMarkers.filter((m) => (m.region || "").toLowerCase() === regionLabel);
  }, [locationMarkers, locationRegion]);
  const countryHeat = useMemo(() => {
    const map = {};
    const isoToName = {
      de: "germany",
      fr: "france",
      es: "spain",
      it: "italy",
      at: "austria",
      ch: "switzerland",
      be: "belgium",
      nl: "netherlands",
      lu: "luxembourg",
      dk: "denmark",
      se: "sweden",
      no: "norway",
      fi: "finland",
      pl: "poland",
      cz: "czechia",
      sk: "slovakia",
      si: "slovenia",
      hr: "croatia",
      hu: "hungary",
      ro: "romania",
      bg: "bulgaria",
      gr: "greece",
      pt: "portugal",
      ie: "ireland",
      gb: "united kingdom",
      uk: "united kingdom",
      us: "united states",
      ca: "canada",
      mx: "mexico",
      br: "brazil",
      ar: "argentina",
      cl: "chile",
      co: "colombia",
      pe: "peru",
      uy: "uruguay",
      py: "paraguay",
      cn: "china",
      jp: "japan",
      kr: "south korea",
      in: "india",
      sg: "singapore",
      my: "malaysia",
      th: "thailand",
      vn: "vietnam",
      ph: "philippines",
      id: "indonesia",
      au: "australia",
      nz: "new zealand",
    };
    (analytics?.location_countries || []).forEach((row) => {
      const label = (row.label || "").toLowerCase();
      const name = isoToName[label] || label;
      map[name] = row.value;
    });
    return map;
  }, [analytics]);

  const renderModuleCheckboxes = (stateObj, setState) => (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
      {availableModules.map((module) => {
        const checked = !!stateObj[module];
        return (
          <button
            key={module}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 10px",
              borderRadius: 10,
              border: checked ? "1px solid #7BD5F5" : "1px solid rgba(255,255,255,0.12)",
              background: checked ? "rgba(123,213,245,0.12)" : "rgba(255,255,255,0.04)",
              color: "#e9ecf1",
              cursor: "pointer",
              minWidth: 0,
            }}
            type="button"
            onClick={() =>
              setState((prev) => ({
                ...prev,
                modules: { ...(prev.modules || {}), [module]: !checked },
              }))
            }
          >
            <span>{module}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="container-fluid py-4 settings-page">
      <h2 className="mb-3" style={{ color: "#f7f8fa" }}>{tx("Admin Control Panel", "Admin-Kontrollpanel", "管理控制面板")}</h2>
      <p style={{ color: "rgba(255,255,255,0.7)" }}>{tx("Manage users, permissions, and groups. Actions require admin access.", "Verwalten Sie Benutzer, Berechtigungen und Gruppen. Aktionen erfordern Adminrechte.", "管理用户、权限和分组。操作需要管理员权限。")}</p>
      {(message || error) && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 10,
            background: message ? "rgba(78, 189, 132, 0.1)" : "rgba(255, 77, 79, 0.08)",
            color: message ? "#aef8c9" : "#ffb3b3",
            border: message ? "1px solid rgba(78, 189, 132, 0.4)" : "1px solid rgba(255,77,79,0.35)",
          }}
        >
          {message || error}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { key: "analytics", label: tx("Analytics", "Analyse", "分析") },
          { key: "users", label: tx("User", "Benutzer", "用户") },
          { key: "data", label: tx("Data", "Daten", "数据") },
          { key: "news", label: tx("News", "News", "新闻") },
          { key: "reports", label: tx("Reports", "Berichte", "报告") },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "10px 14px",
              borderRadius: 12,
              border: activeTab === tab.key ? "1px solid #7BD5F5" : "1px solid rgba(255,255,255,0.08)",
              background: activeTab === tab.key ? "rgba(123,213,245,0.12)" : "rgba(255,255,255,0.03)",
              color: "#f5f7fa",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "analytics" && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-12">
              <div className="settings-card glass-card-ui" style={{ padding: 18 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ color: "#7BD5F5", fontSize: 12, letterSpacing: 0.3 }}>{tx("KPIs", "KPIs", "关键指标")}</div>
                    <div style={{ color: "#fff", fontSize: 16, fontWeight: 700 }}>{tx("Sessions & Conversions", "Sitzungen & Conversions", "会话与转化")}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end" }}>
                    {["7d", "30d", "90d"].map((r) => (
                      <button
                        key={r}
                        onClick={() => setKpiRange(r)}
                        style={{
                          padding: "6px 10px",
                          borderRadius: 10,
                          border: kpiRange === r ? "1px solid #7BD5F5" : "1px solid rgba(255,255,255,0.1)",
                          background: kpiRange === r ? "rgba(123,213,245,0.12)" : "rgba(255,255,255,0.04)",
                          color: "#cfe4ff",
                          cursor: "pointer",
                        }}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginTop: 12, display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))" }}>
                    {[
                      { label: tx("Sessions", "Sitzungen", "会话"), value: analytics?.kpis?.sessions, fmt: numberFmt },
                      { label: tx("Users", "Nutzer", "用户"), value: analytics?.kpis?.users, fmt: numberFmt },
                      { label: tx("New users", "Neue Nutzer", "新用户"), value: analytics?.kpis?.new_users, fmt: numberFmt },
                      { label: tx("Pageviews / Session", "Seitenaufrufe / Sitzung", "每会话浏览量"), value: analytics?.kpis?.pageviews_per_session, fmt: (v) => numberFmt(v, { maximumFractionDigits: 2 }) },
                      { label: tx("Avg. session duration", "Ø Sitzungsdauer", "平均会话时长"), value: analytics?.kpis?.avg_session_duration, fmt: durationFmt },
                      { label: tx("Online", "Online", "在线"), value: analytics?.kpis?.online_users, fmt: numberFmt },
                      { label: tx("Conversions", "Conversions", "转化"), value: analytics?.kpis?.conversions, fmt: numberFmt },
                      { label: tx("Conversion rate", "Conversion-Rate", "转化率"), value: analytics?.kpis?.conversion_rate, fmt: percentFmt },
                    ].map((m) => (
                    <div key={m.label} style={{ color: "#fff" }}>
                      <div style={{ opacity: 0.7, fontSize: 12 }}>{m.label}</div>
                      <div style={{ fontSize: 18, fontWeight: 700 }}>{(m.fmt || numberFmt)(m.value)}</div>
                    </div>
                  ))}
                </div>
                <div style={{ marginTop: 16, background: "#0c111a", borderRadius: 12, padding: 10, height: 240, position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", top: 8, left: 10, color: "#cfe4ff", fontWeight: 700, fontSize: 12 }}>
                    {tx("Metric selection", "Metrik-Auswahl", "指标选择")}
                  </div>
                  <div style={{ position: "absolute", top: 8, right: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {[
                      { key: "sessions", label: tx("Sessions", "Sitzungen", "会话") },
                      { key: "active_users", label: tx("Active Users", "Aktive Nutzer", "活跃用户") },
                      { key: "new_users", label: tx("New Users", "Neue Nutzer", "新用户") },
                      { key: "conversions", label: tx("Conversions", "Conversions", "转化") },
                      { key: "pageviews", label: tx("Pageviews", "Seitenaufrufe", "浏览量") },
                    ].map((m) => (
                      <label key={m.key} style={{ color: "#cfe4ff", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                        <input
                          type="checkbox"
                          checked={metricSelection.includes(m.key)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setMetricSelection((prev) => {
                              if (checked) return Array.from(new Set([...prev, m.key]));
                              return prev.filter((x) => x !== m.key);
                            });
                          }}
                        />
                        {m.label}
                      </label>
                    ))}
                  </div>
                  {timeSeries.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={filteredTimeSeries} margin={{ top: 26, right: 12, left: 0, bottom: 6 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                        <XAxis
                          dataKey="ts"
                          tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 11 }}
                          tickFormatter={(v) => new Date(v).toLocaleDateString(locale, { month: "short", day: "numeric" })}
                          minTickGap={16}
                        />
                        <YAxis stroke="rgba(255,255,255,0.4)" />
                        <Tooltip contentStyle={{ background: "#0f1622", border: "1px solid rgba(255,255,255,0.08)" }} />
                        {metricSelection.includes("sessions") && (
                          <Line type="monotone" dataKey="sessions" stroke={chartColors[0]} strokeWidth={2} dot={false} name={tx("Sessions", "Sitzungen", "会话")} />
                        )}
                        {metricSelection.includes("active_users") && (
                          <Line type="monotone" dataKey="active_users" stroke={chartColors[1]} strokeWidth={2} dot={false} name={tx("Active Users", "Aktive Nutzer", "活跃用户")} />
                        )}
                        {metricSelection.includes("new_users") && (
                          <Line type="monotone" dataKey="new_users" stroke={chartColors[2]} strokeWidth={2} dot={false} name={tx("New Users", "Neue Nutzer", "新用户")} />
                        )}
                        {metricSelection.includes("conversions") && (
                          <Line type="monotone" dataKey="conversions" stroke={chartColors[3]} strokeWidth={2} dot={false} name={tx("Conversions", "Conversions", "转化")} />
                        )}
                        {metricSelection.includes("pageviews") && (
                          <Line type="monotone" dataKey="pageviews" stroke={chartColors[0]} strokeWidth={1.6} strokeDasharray="4 3" dot={false} name={tx("Pageviews", "Seitenaufrufe", "浏览量")} />
                        )}
                        <Legend />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ position: "absolute", inset: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.35)", fontSize: 12 }}>
                      {tx("No data yet", "Noch keine Daten", "暂无数据")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-lg-8">
              <div className="settings-card glass-card-ui" style={{ height: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <div style={{ color: "#7BD5F5", fontSize: 12, letterSpacing: 0.3 }}>{tx("User Locations", "Benutzerstandorte", "用户位置")}</div>
                    <div style={{ color: "#fff", fontWeight: 700 }}>{tx("Access behavior", "Zugriffsverhalten", "访问行为")}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    {[
                      { key: "world", label: tx("World", "Welt", "全球") },
                      { key: "eu", label: "EU" },
                      { key: "america", label: tx("America", "Amerika", "美洲") },
                      { key: "asia", label: tx("Asia", "Asien", "亚洲") },
                    ].map((item) => {
                      const active = locationRegion === item.key;
                      return (
                        <button
                          key={item.key}
                          onClick={() => setLocationRegion(item.key)}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 10,
                            border: active ? "1px solid #7BD5F5" : "1px solid rgba(255,255,255,0.08)",
                            background: active ? "rgba(123,213,245,0.12)" : "rgba(255,255,255,0.04)",
                            color: "#cfe4ff",
                            cursor: "pointer",
                          }}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div style={{ height: 800, borderRadius: 18, overflow: "hidden", position: "relative", background: "#0c111a" }}>
                  <ComposableMap
                    projectionConfig={{ scale: 160 }}
                    style={{ width: "100%", height: "100%" }}
                  >
                    <Geographies geography={geoUrl}>
                      {({ geographies }) =>
                        geographies.map((geo) => {
                          const name = (geo.properties?.name || geo.properties?.NAME || "").toLowerCase();
                          const val = countryHeat[name] || 0;
                          // heat color from 0 to 100
                          const intensity = Math.min(1, val / 50); // scale
                          const base = [123, 213, 245]; // light blue
                          const fill = `rgba(${base[0]}, ${base[1]}, ${base[2]}, ${0.05 + intensity * 0.6})`;
                          return (
                            <Geography
                              key={geo.rsmKey}
                              geography={geo}
                              fill={fill}
                              stroke="rgba(255,255,255,0.06)"
                              strokeWidth={0.4}
                            />
                          );
                        })
                      }
                    </Geographies>
                    {filteredMarkers.map((m) => (
                      <Marker key={`${m.name}-${m.region}`} coordinates={m.coordinates}>
                        <circle r={4} fill="#7BD5F5" stroke="#08111b" strokeWidth={1} />
                        <text
                          textAnchor="middle"
                          y={-8}
                          style={{ fill: "#cfe4ff", fontSize: 10, pointerEvents: "none" }}
                        >
                          {`${m.name} (${m.value.toFixed ? m.value.toFixed(2) : m.value})`}
                        </text>
                      </Marker>
                    ))}
                  </ComposableMap>
                </div>
                <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }}>
                  {(locationRegion === "world" ? regionMeta : regionMeta.filter((r) => r.label.toLowerCase() === locationRegion)).map((blk) => (
                    <div key={blk.key} style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ color: "#fff", fontWeight: 700, marginBottom: 6 }}>{blk.label}</div>
                      {(analytics?.locations?.[blk.key] || []).length === 0 && (
                        <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{tx("No data", "Keine Daten", "无数据")}</div>
                      )}
                      {(analytics?.locations?.[blk.key] || []).map(([name, val]) => (
                        <div key={name} style={{ display: "flex", justifyContent: "space-between", color: "#cfe4ff", fontSize: 13 }}>
                          <span>{name}</span>
                          <span>{val}%</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-4">
              <div className="settings-card glass-card-ui" style={{ marginBottom: 12, background: "rgba(10, 14, 22, 0.9)", border: "1px solid rgba(255,255,255,0.05)" }}>
                <div style={{ color: "#7BD5F5", fontSize: 12, letterSpacing: 0.3 }}>{tx("Server", "Server", "服务器")}</div>
                <div style={{ color: "#fff", fontWeight: 700, marginBottom: 12 }}>{tx("System metrics", "Systemmetriken", "系统指标")}</div>
                <div style={{ background: "#0f1622", borderRadius: 10, padding: 10, color: "#cfe4ff" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 8 }}>
                    {[
                      { label: "CPU", value: percentFmt(serverLatest.cpu) },
                      { label: "RAM", value: serverLatest.ram ? `${numberFmt(serverLatest.ram)} MB / ${percentFmt(serverLatest.ram_pct)}` : percentFmt(serverLatest.ram_pct) },
                      { label: "Disk", value: percentFmt(serverLatest.disk) },
                      { label: "Net", value: serverLatest.net ? `${numberFmt(serverLatest.net)} MB/s` : "—" },
                      { label: tx("Uptime", "Laufzeit", "运行时长"), value: durationFmt(serverLatest.uptime_seconds) },
                    ].map((m) => (
                      <div key={m.label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: 8 }}>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>{m.label}</div>
                        <div style={{ fontWeight: 700 }}>{m.value}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 12, height: 180, background: "rgba(255,255,255,0.02)", borderRadius: 12, position: "relative", overflow: "hidden", border: "1px dashed rgba(255,255,255,0.08)" }}>
                    {serverSeriesData.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={serverSeriesData} margin={{ top: 16, right: 16, left: 16, bottom: 12 }}>
                          <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                          <XAxis
                            dataKey="ts"
                            tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 10 }}
                            tickFormatter={(v) => new Date(v).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })}
                            minTickGap={20}
                          />
                          <YAxis stroke="rgba(255,255,255,0.35)" domain={[0, "auto"]} allowDecimals />
                          <Tooltip
                            contentStyle={{ background: "#0f1622", border: "1px solid rgba(255,255,255,0.08)" }}
                            formatter={(value, name) => {
                              if (typeof value === "number") {
                                return [Number(value).toFixed(2), name];
                              }
                              return [value, name];
                            }}
                          />
                          <Line type="monotone" dataKey="cpu" stroke={chartColors[0]} strokeWidth={2.2} dot={false} name="CPU %" />
                          <Line type="monotone" dataKey="disk" stroke={chartColors[1]} strokeWidth={2.2} dot={false} name={tx("Disk %", "Disk %", "磁盘 %")} />
                          <Line type="monotone" dataKey="net" stroke={chartColors[2]} strokeWidth={2.2} dot={false} name={tx("Net MB/s", "Netz MB/s", "网络 MB/s")} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.5)", fontSize: 12 }}>
                        {tx("No server metrics yet", "Noch keine Servermetriken", "暂无服务器指标")}
                      </div>
                    )}
                  </div>
                  </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 12 }}>
                {[{ title: tx("Devices", "Geräte", "设备"), data: deviceChartData }, { title: tx("Browser", "Browser", "浏览器"), data: browserChartData }].map((blk, idx) => (
                  <div key={blk.title} className="settings-card glass-card-ui">
                    <div style={{ color: "#fff", fontWeight: 700, marginBottom: 8 }}>{blk.title}</div>
                    {blk.data && blk.data.length > 0 ? (
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie data={blk.data} dataKey="value" nameKey="label" innerRadius={50} outerRadius={70} paddingAngle={4}>
                            {blk.data.map((entry, i) => (
                              <Cell key={`cell-${blk.title}-${entry.label}`} fill={chartColors[i % chartColors.length]} />
                            ))}
                          </Pie>
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{tx("No data", "Keine Daten", "无数据")}</div>
                    )}
                  </div>
                ))}
              </div>

              <div className="settings-card glass-card-ui">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ color: "#7BD5F5", fontSize: 12, letterSpacing: 0.3 }}>{tx("AI Usage", "KI-Nutzung", "AI 使用")}</div>
                    <div style={{ color: "#fff", fontWeight: 700 }}>{tx("Prompts & Errors", "Prompts & Fehler", "提示词与错误")}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", maxWidth: 180 }}>
                    {["1h", "3h", "12h", "1d", "1w", "1m", "3m", "5y", "max"].map((t) => (
                      <button key={t} style={{ padding: "4px 8px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.04)", color: "#cfe4ff", cursor: "pointer", fontSize: 11 }}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", marginTop: 12 }}>
                  <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10 }}>
                    <div style={{ color: "#fff", fontSize: 22, fontWeight: 700 }}>{numberFmt(aiUsage.prompts_day)}</div>
                    <div style={{ color: "#cfe4ff", fontSize: 12 }}>{tx("AI Prompts (Day)", "KI-Prompts (Tag)", "AI 提示词（日）")}</div>
                    <div style={{ color: "#fff", fontSize: 22, fontWeight: 700, marginTop: 4 }}>{numberFmt(aiUsage.prompts_week)}</div>
                    <div style={{ color: "#cfe4ff", fontSize: 12 }}>{tx("AI Prompts (Week)", "KI-Prompts (Woche)", "AI 提示词（周）")}</div>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10 }}>
                    <div style={{ color: "#fff", fontSize: 22, fontWeight: 700 }}>{percentFmt(aiUsage.prompt_quality)}</div>
                    <div style={{ color: "#cfe4ff", fontSize: 12 }}>{tx("Prompt Quality", "Prompt-Qualität", "提示词质量")}</div>
                  </div>
                  <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10 }}>
                    <div style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>{numberFmt(aiUsage.errors)} {tx("Errors", "Fehler", "错误")}</div>
                    <div style={{ color: "#fff", fontSize: 18, fontWeight: 700, marginTop: 4 }}>{numberFmt(aiUsage.timeouts)} {tx("Timeouts", "Timeouts", "超时")}</div>
                  </div>
                </div>
                <div style={{ marginTop: 12 }}>
                  <div style={{ color: "#fff", fontWeight: 700, marginBottom: 6 }}>{tx("AI Feature Usage", "KI-Feature-Nutzung", "AI 功能使用")}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", color: "#cfe4ff", fontSize: 13, gap: 6 }}>
                    <div style={{ opacity: 0.7 }}>{tx("Feature", "Feature", "功能")}</div>
                    <div style={{ opacity: 0.7, textAlign: "right" }}>%</div>
                    <div style={{ opacity: 0.7, textAlign: "right" }}>{tx("Avg. Token", "Ø Token", "平均 Token")}</div>
                    {(analytics?.ai_usage?.feature_usage || []).map((row) => (
                      <div key={row.feature} style={{ display: "contents" }}>
                        <div>{row.feature}</div>
                        <div style={{ textAlign: "right" }}>{row.percent ?? "—"}%</div>
                        <div style={{ textAlign: "right" }}>{row.avg_token ?? "—"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <AdminDataTab
        activeTab={activeTab}
        tx={tx}
        setDataRange={setDataRange}
        dataRange={dataRange}
        fetchDataMetrics={fetchDataMetrics}
        buttonStyle={buttonStyle}
        dataLoading={dataLoading}
        dataError={dataError}
        dataMetrics={dataMetrics}
        numberFmt={numberFmt}
        locale={locale}
        chartColors={chartColors}
        insightsFallbackUsed={insightsFallbackUsed}
        insightsFallbackReason={insightsFallbackReason}
        insightsWindowDays={insightsWindowDays}
        insightsLoading={insightsLoading}
        insightsError={insightsError}
        materialInsights={materialInsights}
        selectedMaterial={selectedMaterial}
        setSelectedMaterial={setSelectedMaterial}
        fetchMaterialDetail={fetchMaterialDetail}
        materialDetail={materialDetail}
        materialPipelineStatus={materialPipelineStatus}
        materialPipelineStatusLoading={materialPipelineStatusLoading}
        materialPipelineStatusError={materialPipelineStatusError}
        fetchMaterialPipelineStatus={fetchMaterialPipelineStatus}
        sectorsError={sectorsError}
        fetchSectors={fetchSectors}
        sectorsLoading={sectorsLoading}
        sectors={sectors}
        selectedSectorId={selectedSectorId}
        loadSectorDetail={loadSectorDetail}
        translationButton={translationButton}
        createSector={createSector}
        sectorForm={sectorForm}
        setSectorForm={setSectorForm}
        fieldStyle={fieldStyle}
        labelStyle={labelStyle}
        inputStyle={inputStyle}
        selectStyle={selectStyle}
        sectorDetail={sectorDetail}
        sectorDetailLoading={sectorDetailLoading}
        deleteSector={deleteSector}
        sectorEditForm={sectorEditForm}
        setSectorEditForm={setSectorEditForm}
        updateSector={updateSector}
        removeMaterialFromSector={removeMaterialFromSector}
        materialSearch={materialSearch}
        searchMaterials={searchMaterials}
        materialAddTarget={materialAddTarget}
        setMaterialAddTarget={setMaterialAddTarget}
        materialSearchLoading={materialSearchLoading}
        materialSearchError={materialSearchError}
        materialSearchResults={materialSearchResults}
        addMaterialToSector={addMaterialToSector}
        addMaterialToList={addMaterialToList}
        onListDragEnd={onListDragEnd}
        listDrafts={listDrafts}
        setListDrafts={setListDrafts}
        saveListDraft={saveListDraft}
        deleteList={deleteList}
        removeMaterialFromList={removeMaterialFromList}
        listForm={listForm}
        setListForm={setListForm}
        createList={createList}
        translationModalOpen={translationModalOpen}
        setTranslationModalOpen={setTranslationModalOpen}
        translationSourceText={translationSourceText}
        translationLoading={translationLoading}
        translationInputs={translationInputs}
        setTranslationInputs={setTranslationInputs}
        translationError={translationError}
        translationSaving={translationSaving}
        saveTranslationModal={saveTranslationModal}
      />

      {activeTab === "news" && (
        <div className="row g-3">
          <div className="col-12">
            <div className="settings-card glass-card-ui">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <div style={{ color: "#7BD5F5", fontSize: 12, letterSpacing: 0.3 }}>{tx("News", "News", "新闻")}</div>
                  <div style={{ color: "#fff", fontWeight: 700 }}>{tx("Coverage & Production", "Abdeckung & Produktion", "覆盖率与产出")}</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {["7d", "30d", "90d"].map((r) => (
                    <button
                      key={r}
                      onClick={() => setNewsRange(r)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 10,
                        border: newsRange === r ? "1px solid #7BD5F5" : "1px solid rgba(255,255,255,0.1)",
                        background: newsRange === r ? "rgba(123,213,245,0.12)" : "rgba(255,255,255,0.04)",
                        color: "#cfe4ff",
                        cursor: "pointer",
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              {newsLoading && <div style={{ color: "#cfe4ff", fontSize: 12, marginTop: 10 }}>{tx("Loading…", "Lädt…", "加载中…")}</div>}
              {newsError && <div style={{ color: "#ffb3b3", fontSize: 12, marginTop: 10 }}>{newsError}</div>}
              {newsMetrics && (
                <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                    {[
                      { label: tx("Total display articles", "Display-Artikel gesamt", "展示文章总数"), value: newsMetrics.summary?.total_articles },
                      { label: tx("Versions", "Versionen", "版本"), value: newsMetrics.summary?.total_versions },
                      { label: tx("Source links", "Quellen-Links", "来源关联"), value: newsMetrics.summary?.total_source_links },
                      { label: tx("Material links", "Material-Links", "材料关联"), value: newsMetrics.summary?.total_material_links },
                      { label: tx("Avg. sources / article", "Ø Quellen / Artikel", "每篇平均来源"), value: newsMetrics.summary?.avg_sources_per_article },
                      { label: tx("Avg. match rate", "Ø Match-Rate", "平均匹配率"), value: `${numberFmt(newsMetrics.summary?.avg_match_rate_pct, { maximumFractionDigits: 2 })}%` },
                      { label: tx("Views (30d)", "Aufrufe (30d)", "浏览量（30天）"), value: newsMetrics.summary?.views_30d },
                      { label: tx("Translated EN / ZH", "Übersetzt EN / ZH", "已翻译 EN / ZH"), value: `${numberFmt(newsMetrics.summary?.translated_en, { maximumFractionDigits: 0 })} / ${numberFmt(newsMetrics.summary?.translated_zh, { maximumFractionDigits: 0 })}` },
                    ].map((m) => (
                      <div key={m.label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10, color: "#fff" }}>
                        <div style={{ opacity: 0.7, fontSize: 12 }}>{m.label}</div>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>{typeof m.value === "string" ? m.value : numberFmt(m.value, { maximumFractionDigits: 2 })}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ height: 280, background: "#0c111a", borderRadius: 12, padding: 10, position: "relative" }}>
                    <div style={{ position: "absolute", top: 8, left: 10, color: "#cfe4ff", fontWeight: 700, fontSize: 12 }}>
                      {tx("Daily news pipeline", "Tägliche News-Pipeline", "每日新闻流程")}
                    </div>
                    {newsMetrics.time_series?.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={newsMetrics.time_series} margin={{ top: 26, right: 12, left: 0, bottom: 6 }}>
                          <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                          <XAxis
                            dataKey="ts"
                            tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 11 }}
                            tickFormatter={(v) => new Date(v).toLocaleDateString(locale, { month: "short", day: "numeric" })}
                            minTickGap={16}
                          />
                          <YAxis stroke="rgba(255,255,255,0.4)" />
                          <Tooltip contentStyle={{ background: "#0f1622", border: "1px solid rgba(255,255,255,0.08)" }} />
                          <Line type="monotone" dataKey="created_articles" stroke={chartColors[0]} strokeWidth={2} dot={false} name={tx("Created", "Erstellt", "创建")} />
                          <Line type="monotone" dataKey="source_links" stroke={chartColors[1]} strokeWidth={2} dot={false} name={tx("Source links", "Quellen-Links", "来源关联")} />
                          <Line type="monotone" dataKey="views" stroke={chartColors[2]} strokeWidth={2} dot={false} name={tx("Views", "Aufrufe", "浏览")} />
                          <Line type="monotone" dataKey="translations_completed" stroke={chartColors[3]} strokeWidth={2} dot={false} name={tx("Translations", "Übersetzungen", "翻译")} />
                          <Legend />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ position: "absolute", inset: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.35)", fontSize: 12 }}>
                        {tx("No data yet", "Noch keine Daten", "暂无数据")}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "reports" && (
        <div className="row g-3">
          <div className="col-12 col-xl-8">
            <div className="settings-card glass-card-ui">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <div style={{ color: "#7BD5F5", fontSize: 12, letterSpacing: 0.3 }}>{tx("Reports", "Berichte", "报告")}</div>
                  <div style={{ color: "#fff", fontWeight: 700 }}>{tx("Generation & Usage", "Generierung & Nutzung", "生成与使用")}</div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {["7d", "30d", "90d"].map((r) => (
                    <button
                      key={r}
                      onClick={() => setReportsRange(r)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 10,
                        border: reportsRange === r ? "1px solid #7BD5F5" : "1px solid rgba(255,255,255,0.1)",
                        background: reportsRange === r ? "rgba(123,213,245,0.12)" : "rgba(255,255,255,0.04)",
                        color: "#cfe4ff",
                        cursor: "pointer",
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
              {reportsLoading && <div style={{ color: "#cfe4ff", fontSize: 12, marginTop: 10 }}>{tx("Loading…", "Lädt…", "加载中…")}</div>}
              {reportsError && <div style={{ color: "#ffb3b3", fontSize: 12, marginTop: 10 }}>{reportsError}</div>}
              {reportsMetrics && (
                <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
                    {[
                      { label: tx("Total reports", "Berichte gesamt", "报告总数"), value: reportsMetrics.summary?.total_reports },
                      { label: tx("Published / Draft", "Publiziert / Entwurf", "已发布 / 草稿"), value: `${numberFmt(reportsMetrics.summary?.published_reports, { maximumFractionDigits: 0 })} / ${numberFmt(reportsMetrics.summary?.draft_reports, { maximumFractionDigits: 0 })}` },
                      { label: tx("Avg. read time (min)", "Ø Lesezeit (Min)", "平均阅读时长（分钟）"), value: reportsMetrics.summary?.avg_read_time_minutes },
                      { label: tx("Customizations", "Anpassungen", "自定义改写"), value: reportsMetrics.summary?.customizations_total },
                      { label: tx("Avg. match score", "Ø Match-Score", "平均匹配分"), value: `${numberFmt(reportsMetrics.summary?.avg_match_score_pct, { maximumFractionDigits: 2 })}%` },
                      { label: tx("Views (30d)", "Aufrufe (30d)", "浏览量（30天）"), value: reportsMetrics.summary?.views_30d },
                      { label: tx("Translated EN / ZH", "Übersetzt EN / ZH", "已翻译 EN / ZH"), value: `${numberFmt(reportsMetrics.summary?.translated_en, { maximumFractionDigits: 0 })} / ${numberFmt(reportsMetrics.summary?.translated_zh, { maximumFractionDigits: 0 })}` },
                    ].map((m) => (
                      <div key={m.label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: 10, color: "#fff" }}>
                        <div style={{ opacity: 0.7, fontSize: 12 }}>{m.label}</div>
                        <div style={{ fontSize: 18, fontWeight: 700 }}>{typeof m.value === "string" ? m.value : numberFmt(m.value, { maximumFractionDigits: 2 })}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ height: 280, background: "#0c111a", borderRadius: 12, padding: 10, position: "relative" }}>
                    <div style={{ position: "absolute", top: 8, left: 10, color: "#cfe4ff", fontWeight: 700, fontSize: 12 }}>
                      {tx("Daily report pipeline", "Tägliche Berichts-Pipeline", "每日报告流程")}
                    </div>
                    {reportsMetrics.time_series?.length ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={reportsMetrics.time_series} margin={{ top: 26, right: 12, left: 0, bottom: 6 }}>
                          <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                          <XAxis
                            dataKey="ts"
                            tick={{ fill: "rgba(255,255,255,0.7)", fontSize: 11 }}
                            tickFormatter={(v) => new Date(v).toLocaleDateString(locale, { month: "short", day: "numeric" })}
                            minTickGap={16}
                          />
                          <YAxis stroke="rgba(255,255,255,0.4)" />
                          <Tooltip contentStyle={{ background: "#0f1622", border: "1px solid rgba(255,255,255,0.08)" }} />
                          <Line type="monotone" dataKey="created_reports" stroke={chartColors[0]} strokeWidth={2} dot={false} name={tx("Created", "Erstellt", "创建")} />
                          <Line type="monotone" dataKey="published_reports" stroke={chartColors[1]} strokeWidth={2} dot={false} name={tx("Published", "Publiziert", "发布")} />
                          <Line type="monotone" dataKey="views" stroke={chartColors[2]} strokeWidth={2} dot={false} name={tx("Views", "Aufrufe", "浏览")} />
                          <Line type="monotone" dataKey="translations_completed" stroke={chartColors[3]} strokeWidth={2} dot={false} name={tx("Translations", "Übersetzungen", "翻译")} />
                          <Legend />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ position: "absolute", inset: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "rgba(255,255,255,0.35)", fontSize: 12 }}>
                        {tx("No data yet", "Noch keine Daten", "暂无数据")}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="col-12 col-xl-4">
            <div className="settings-card glass-card-ui">
              <div style={{ color: "#fff", fontWeight: 700, marginBottom: 8 }}>{tx("Report status", "Berichtsstatus", "报告状态")}</div>
              {reportsStatusChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={reportsStatusChartData} dataKey="value" nameKey="label" innerRadius={52} outerRadius={80} paddingAngle={4}>
                      {reportsStatusChartData.map((entry, i) => (
                        <Cell key={`report-status-${entry.label}`} fill={chartColors[i % chartColors.length]} />
                      ))}
                    </Pie>
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{tx("No status data", "Keine Statusdaten", "暂无状态数据")}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "users" && (
        <div className="row g-3">
          <div className="col-12 col-lg-3">
            <div className="settings-card glass-card-ui">
              <h5 style={{ color: "#fff", marginBottom: 12 }}>{tx("Users", "Benutzer", "用户")}</h5>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
                <input
                  type="text"
                  placeholder={tx("Search name or email", "Name oder E-Mail suchen", "搜索姓名或邮箱")}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  style={{ ...inputStyle, width: "100%" }}
                />
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ ...labelStyle, marginBottom: -4 }}>{tx("Role", "Rolle", "角色")}</label>
                  <select
                    value={userFilters.role}
                    onChange={(e) => setUserFilters((prev) => ({ ...prev, role: e.target.value }))}
                    style={selectStyle}
                  >
                    <option value="all">{tx("All", "Alle", "全部")}</option>
                    {roles.map((r) => (
                      <option key={r.value} value={r.value.toLowerCase()}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ ...labelStyle, marginBottom: -4 }}>{tx("Module", "Modul", "模块")}</label>
                  <select
                    value={userFilters.module}
                    onChange={(e) => setUserFilters((prev) => ({ ...prev, module: e.target.value }))}
                    style={selectStyle}
                  >
                    <option value="all">{tx("All", "Alle", "全部")}</option>
                    {availableModules.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ ...labelStyle, marginBottom: -4 }}>{tx("Status", "Status", "状态")}</label>
                  <select
                    value={userFilters.status}
                    onChange={(e) => setUserFilters((prev) => ({ ...prev, status: e.target.value }))}
                    style={selectStyle}
                  >
                    <option value="all">{tx("All", "Alle", "全部")}</option>
                    {["active", "inactive", "initializing"].map((s) => (
                      <option key={s} value={s}>
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ ...labelStyle, marginBottom: -4 }}>{tx("Online", "Online", "在线")}</label>
                  <select
                    value={userFilters.online}
                    onChange={(e) => setUserFilters((prev) => ({ ...prev, online: e.target.value }))}
                    style={selectStyle}
                  >
                    <option value="all">{tx("All", "Alle", "全部")}</option>
                    <option value="online">{tx("Online", "Online", "在线")}</option>
                    <option value="offline">{tx("Offline", "Offline", "离线")}</option>
                  </select>
                </div>
              </div>
              <div style={{ maxHeight: 520, overflow: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
                {users.length === 0 && <div style={{ color: "rgba(255,255,255,0.6)" }}>{tx("No users loaded.", "Keine Benutzer geladen.", "未加载到用户。")}</div>}
                {filteredUsers.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => applyUserSelection(u)}
                    style={{
                      textAlign: "left",
                      border: "1px solid rgba(255,255,255,0.08)",
                      background: u.id === selectedUserId ? "rgba(75,134,247,0.18)" : "rgba(255,255,255,0.03)",
                      color: "#f1f5fa",
                      borderRadius: 12,
                      padding: 10,
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ fontWeight: 700 }}>{u.first_name} {u.last_name}</div>
                      <div style={{ display: "flex", gap: 6 }}>
                        {renderStatusBadge(u)}
                        {renderOnlineBadge(u)}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>{u.email}</div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>{tx("Role", "Rolle", "角色")}: {u.role || tx("user", "Benutzer", "用户")}</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>{tx("Modules", "Module", "模块")}: {(u.allowed_modules || []).join(", ") || "—"}</div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>{tx("Last online", "Zuletzt online", "最近在线")}: {u.last_seen_at ? new Date(u.last_seen_at).toLocaleString(locale) : "—"}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-9">
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
              <div className="settings-card glass-card-ui">
                <h5 style={{ color: "#fff", marginBottom: 12 }}>{tx("Selected user", "Ausgewählter Benutzer", "已选用户")}</h5>
                {selectedUser ? (
                  <>
                    <div style={{ color: "#fff", fontWeight: 700, marginBottom: 4 }}>
                      {selectedUser.first_name} {selectedUser.last_name}
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.75)", fontSize: 13 }}>{selectedUser.email}</div>
                    <div style={{ color: "#cfe4ff", fontSize: 12, marginTop: 8 }}>
                      {tx("Role", "Rolle", "角色")}: {selectedUser.role || "—"}
                    </div>
                    <div style={{ color: "#cfe4ff", fontSize: 12 }}>
                      {tx("Modules", "Module", "模块")}: {(selectedUser.allowed_modules || []).join(", ") || "—"}
                    </div>
                    <div style={{ color: "#cfe4ff", fontSize: 12, marginTop: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {renderStatusBadge(selectedUser)}
                      {renderOnlineBadge(selectedUser)}
                      <span>{tx("Last online", "Zuletzt online", "最近在线")}: {selectedUser.last_seen_at ? new Date(selectedUser.last_seen_at).toLocaleString(locale) : "—"}</span>
                    </div>
                  </>
                ) : (
                  <div style={{ color: "rgba(255,255,255,0.6)" }}>{tx("Select a user to manage.", "Wählen Sie einen Benutzer zur Verwaltung.", "请选择要管理的用户。")}</div>
                )}
              </div>

              <div className="settings-card glass-card-ui">
                <h5 style={{ color: "#fff", marginBottom: 12 }}>{tx("Set user role", "Benutzerrolle festlegen", "设置用户角色")}</h5>
                <form onSubmit={handleUserRoleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>{tx("Role", "Rolle", "角色")}</label>
                    <select
                      style={selectStyle}
                      value={userRoleForm.role}
                      onChange={(e) => setUserRoleForm((prev) => ({ ...prev, role: e.target.value }))}
                    >
                      {roles.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <button style={buttonStyle} type="submit" disabled={loading || !selectedUserId}>
                    {tx("Save role", "Rolle speichern", "保存角色")}
                  </button>
                </form>
              </div>

              <div className="settings-card glass-card-ui">
                <h5 style={{ color: "#fff", marginBottom: 12 }}>{tx("Connections", "Verbindungen", "连接")}</h5>
                {connectionsLoading ? (
                  <div style={{ color: "#cfe4ff", fontSize: 12 }}>{tx("Loading connections…", "Verbindungen werden geladen…", "正在加载连接…")}</div>
                ) : connectionsError ? (
                  <div style={{ color: "#ffb3b3", fontSize: 12 }}>{connectionsError}</div>
                ) : userConnections.connections && userConnections.connections.length > 0 ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.6fr 1fr", gap: 6, color: "#cfe4ff", fontSize: 12 }}>
                    <div style={{ opacity: 0.7 }}>{tx("IP address", "IP-Adresse", "IP 地址")}</div>
                    <div style={{ opacity: 0.7, textAlign: "right" }}>{tx("Hits", "Treffer", "次数")}</div>
                    <div style={{ opacity: 0.7, textAlign: "right" }}>{tx("Last seen", "Zuletzt gesehen", "最近出现")}</div>
                    {userConnections.connections.map((c, idx) => (
                      <div key={`${c.ip_address || "unknown"}-${idx}`} style={{ display: "contents" }}>
                        <div>{c.ip_address || "—"}</div>
                        <div style={{ textAlign: "right" }}>{c.count}</div>
                        <div style={{ textAlign: "right" }}>{c.last_seen ? new Date(c.last_seen).toLocaleString() : "—"}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 12 }}>{tx("No connection data yet.", "Noch keine Verbindungsdaten.", "暂无连接数据。")}</div>
                )}
              </div>

              <div className="settings-card glass-card-ui">
                <h5 style={{ color: "#fff", marginBottom: 12 }}>{tx("User module overrides", "Benutzermodul-Overrides", "用户模块覆盖")}</h5>
                <form onSubmit={handleUserModulesSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>{tx("Allowed modules", "Erlaubte Module", "允许的模块")}</label>
                    {renderModuleCheckboxes(userModulesForm.modules || {}, setUserModulesForm)}
                  </div>
                  <button style={buttonStyle} type="submit" disabled={loading || !selectedUserId}>
                    {tx("Save modules", "Module speichern", "保存模块")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
